import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Flex, Input, Space, Typography, theme } from 'antd';
import {
  ArrowRightOutlined,
  ExclamationCircleFilled,
  GlobalOutlined,
  LeftOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
// The trailing slash picks the npm package over Node's deprecated builtin.
import { toUnicode } from 'punycode/';

import { useEvent } from '../../events/hooks/useEvent';
import { guidesLocation } from '../integrationGuidesRoute';
import { usePrerenderUserId } from '../../../hooks/usePrerenderUser';
import {
  DOMAIN_REJECTION_MESSAGES,
  DOMAIN_REJECTION_REASONS,
  DomainRejectionReason,
  normalizeDomainInput,
} from '../../../utils/normalizeDomainInput';
import { NexusLogo } from '../../integrationWizard/components/steps/IntegrationStep/Nexus';
import { EVENTS_TRACKED, openHubSpotForm } from '../../CustomerSupport';
import { useProbeDomainMutation } from '../api/cloudflareIntegrationApi';
import {
  CfDomainOwnership,
  CfProbeErrorReason,
  CfProbeResponse,
} from '../api/CfProbeResponse';
import { DEFAULT_ENTRY_SOURCE, cfEventProperties } from '../cfEventProperties';
import { BETA_CLOSED_REASON, BETA_CLOSED_TITLE } from './betaClosedCopy';
import CloudflareStepHeader from './CloudflareStepHeader';
import { useOpenNexusOnTheGuides } from '../useOpenNexusOnTheGuides';

export const CLOUDFLARE_NEXUS_OPTION_LABEL = 'Nexus AI';

const { Title, Paragraph, Text } = Typography;

// FIXME: no exact token — the design's 640px description and 510px form column.
const HEADER_TEXT_WIDTH = 640;
const FORM_COLUMN_WIDTH = 510;

export const CF_EVENTS = {
  entryViewed: 'Automated CF Integration: Entry Viewed',
  domainSubmitted: 'Automated CF Integration: Domain Submitted',
  domainRejected: 'Automated CF Integration: Domain Rejected',
} as const;

const CONTACT_SUPPORT_CLICKED = EVENTS_TRACKED.contactSupportClicked;

type ProbeFailureReason = CfProbeErrorReason | null;

const DEFAULT_PROBE_FAILURE_DESCRIPTION = 'Something went wrong on our side.';

const PROBE_FAILURE_DESCRIPTIONS: Record<
  Exclude<ProbeFailureReason, null>,
  string
> = {
  unreachable: DEFAULT_PROBE_FAILURE_DESCRIPTION,
  nxdomain: 'Confirm that your domain is correct.',
  tls: DEFAULT_PROBE_FAILURE_DESCRIPTION,
  blocked_by_guard: DEFAULT_PROBE_FAILURE_DESCRIPTION,
  http_error:
    "We reached your site, but it returned an error, so we couldn't check it. Make sure it's up and try again.",
};

const probeFailureDescription = (reason: ProbeFailureReason): string =>
  reason === null
    ? DEFAULT_PROBE_FAILURE_DESCRIPTION
    : PROBE_FAILURE_DESCRIPTIONS[reason];

const isDomainRejectionReason = (
  value: unknown,
): value is DomainRejectionReason =>
  typeof value === 'string' &&
  (DOMAIN_REJECTION_REASONS as readonly string[]).includes(value);

// Checked against the closed set: an unknown string would grow the funnel a
// category nobody defined.
function serverRejectionReason(error: unknown): DomainRejectionReason | null {
  const failure = error as { status?: unknown; data?: unknown } | null;
  if (failure?.status !== 400) return null;

  const reason = (failure.data as { reason?: unknown } | null)?.reason;

  return isDomainRejectionReason(reason) ? reason : null;
}

// `CfBetaEntryGuard`'s 403: not a fault, and not fixed by retrying.
function isBetaClosed(error: unknown): boolean {
  return (error as { status?: unknown } | null)?.status === 403;
}

type AlreadySetUp = { ownership: CfDomainOwnership; domain: string };

type Rejection = { reason: DomainRejectionReason; message: string };

const rejectionFor = (reason: DomainRejectionReason): Rejection => ({
  reason,
  message: DOMAIN_REJECTION_MESSAGES[reason],
});

const subdomainRejection = (host: string, root: string): Rejection => ({
  reason: 'subdomain',
  message: `We identified that ${toUnicode(host)} is a subdomain of ${toUnicode(root)}. ${DOMAIN_REJECTION_MESSAGES.subdomain}`,
});

const domainForNexusOf = (typed: string): { domain?: string } => {
  const normalized = normalizeDomainInput(typed);

  return normalized.ok ? { domain: normalized.domain } : {};
};

export type CloudflareEntryStepProps = {
  onGoBack?: () => void;
  onProbed?: (probe: CfProbeResponse) => void;
  onVerifyIntegration?: (domain: string) => void;
};

/** The first screen of the automated Cloudflare flow (PRE-3289 / F1). */
const CloudflareEntryStep = ({
  onGoBack,
  onProbed,
  onVerifyIntegration,
}: CloudflareEntryStepProps) => {
  const { token } = theme.useToken();
  const { track } = useEvent();
  const navigate = useNavigate();
  const location = useLocation();
  const openNexusOnTheGuides = useOpenNexusOnTheGuides();
  const companyId = usePrerenderUserId();
  const [domain, setDomain] = useState('');
  const [rejection, setRejection] = useState<Rejection | null>(null);
  // `undefined` means no failure is shown; `null` is a failure whose reason is
  // ours to own (the request itself never reached a verdict).
  const [probeFailed, setProbeFailed] = useState<ProbeFailureReason | undefined>(
    undefined,
  );
  const [betaClosed, setBetaClosed] = useState(false);
  const [alreadySetUp, setAlreadySetUp] = useState<AlreadySetUp | null>(null);
  const [probeDomain, { isLoading }] = useProbeDomainMutation();

  // `Button`s, not clickable `Card`s: a `Card` with `onClick` is a `div` that
  // the keyboard cannot reach.
  const optionStyle: React.CSSProperties = {
    flex: 1,
    minWidth: token.screenXS,
    height: 'auto',
    padding: token.padding,
    borderRadius: token.borderRadiusLG,
    textAlign: 'left',
    whiteSpace: 'normal',
  };
  const iconBadgeStyle: React.CSSProperties = {
    width: token.sizeXXL,
    height: token.sizeXXL,
    flexShrink: 0,
    // FIXME: no exact token — a circle, not a themed radius.
    borderRadius: '50%',
    border: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
    backgroundColor: token.colorBgContainer,
  };
  // Not `isLoading`: that only turns true on the render after the click, so two
  // clicks in the same tick both read `false` and both start a probe.
  const probeInFlight = useRef(false);

  const eventProperties = (
    reportedDomain: string,
    specific: Record<string, string> = {},
  ) => ({ ...specific, ...cfEventProperties({ domain: reportedDomain }, companyId) });

  useEffect(() => {
    track(CF_EVENTS.entryViewed, eventProperties(''));
    // Once per mount: a re-fire per keystroke would break the funnel's denominator.
  }, []);

  const reject = (refusal: Rejection, reportedDomain: string) => {
    setRejection(refusal);
    setProbeFailed(undefined);
    track(
      CF_EVENTS.domainRejected,
      eventProperties(reportedDomain, { reason: refusal.reason }),
    );
  };

  const onAnalyze = async () => {
    if (probeInFlight.current) return;

    setAlreadySetUp(null);

    const submitted = domain.trim();
    const normalized = normalizeDomainInput(domain);

    if (!normalized.ok) {
      reject(rejectionFor(normalized.reason), submitted);

      return;
    }

    const isSubdomainEntry = normalized.host !== normalized.domain;
    if (isSubdomainEntry) {
      reject(
        subdomainRejection(normalized.host, normalized.domain),
        normalized.host,
      );

      return;
    }

    setRejection(null);
    setProbeFailed(undefined);
    setBetaClosed(false);
    track(CF_EVENTS.domainSubmitted, eventProperties(normalized.domain));

    probeInFlight.current = true;
    try {
      const probe = await probeDomain({
        domain: normalized.domain,
        // Server-side events read `entry_source` from the probe's snapshot.
        entrySource: DEFAULT_ENTRY_SOURCE,
      }).unwrap();

      handleProbeOutcome(probe);
    } catch (error) {
      handleProbeFailure(error, normalized.domain);
    } finally {
      probeInFlight.current = false;
    }
  };

  const handleProbeOutcome = (probe: CfProbeResponse) => {
    // An unreachable domain is `result: 'error'` on an HTTP 200, so watching
    // the status alone leaves a typo'd domain with a screen that says nothing.
    if (probe.result === 'error') {
      setProbeFailed(probe.errorReason);

      return;
    }

    if (probe.result === 'already_integrated' && probe.ownership) {
      setAlreadySetUp({ ownership: probe.ownership, domain: probe.domain });
    }

    onProbed?.(probe);
  };

  const handleProbeFailure = (error: unknown, probedDomain: string) => {
    // The server is the authority on the domain and is reachable without this
    // screen, so a `400 { reason }` is the customer's input, not our failure.
    const reason = serverRejectionReason(error);

    if (reason) {
      reject(rejectionFor(reason), probedDomain);

      return;
    }

    if (isBetaClosed(error)) {
      setBetaClosed(true);

      return;
    }

    setProbeFailed(null);
  };

  return (
    <Flex vertical gap={token.marginLG}>
      <CloudflareStepHeader
        title="Set up automatically with Cloudflare"
        description={
          <>
            We&apos;re testing a new way to integrate: Prerender connects to your
            Cloudflare account and deploys everything for you, no code changes.
            Already on Cloudflare? Setup takes ~5 minutes. Not on Cloudflare yet?
            We&apos;ll walk you through connecting your domain first.
          </>
        }
        descriptionMaxWidth={HEADER_TEXT_WIDTH}
      />

      <Card styles={{ body: { padding: token.paddingXL } }}>
        <Flex vertical gap={token.marginMD} style={{ maxWidth: FORM_COLUMN_WIDTH, margin: '0 auto' }}>
          <Card size="small" style={{ background: token.colorFillAlter }}>
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              Why do I need Cloudflare?
            </Text>
            <Paragraph style={{ marginBottom: 0, fontSize: token.fontSizeLG }}>
              Cloudflare acts as your traffic director: real visitors go straight
              to your site, and crawlers get a fully rendered version of your
              pages from Prerender.
            </Paragraph>
          </Card>

          <Alert
            type="info"
            showIcon
            message="Some setups need a different path"
            description={
              <>
                If your site runs on another CDN (Fastly, Akamai, CloudFront),
                your platform locks DNS, or you use Lovable or Bubble, follow our{' '}
                {/* A Button, not a `Typography.Link`. A link with an
                    `onClick` and no `href` renders a bare `<a>`: no button
                    role, and no way to reach it from the keyboard. */}
                <Button
                  type="link"
                  onClick={() => navigate(guidesLocation(location.search))}
                  style={{ padding: 0 }}
                >
                  dedicated guides
                </Button>{' '}
                instead.
              </>
            }
          />

          <Flex vertical gap={token.marginMD}>
            <Space.Compact block size="large">
              <Input
                id="cf-entry-domain"
                aria-label="Your domain"
                prefix={<GlobalOutlined />}
                placeholder="e.g. yoursite.com"
                value={domain}
                suffix={
                  alreadySetUp ? (
                    <ExclamationCircleFilled style={{ color: token.colorWarning }} />
                  ) : undefined
                }
                status={rejection ? 'error' : alreadySetUp ? 'warning' : undefined}
                aria-invalid={rejection !== null}
                aria-describedby={
                  rejection
                    ? 'cf-entry-domain-error'
                    : alreadySetUp
                      ? 'cf-entry-domain-warning'
                      : undefined
                }
                onChange={(event) => {
                  setDomain(event.target.value);
                  setAlreadySetUp(null);
                }}
                onPressEnter={onAnalyze}
                disabled={isLoading}
              />
              <Button type="primary" onClick={onAnalyze} loading={isLoading}>
                Analyze Domain
              </Button>
            </Space.Compact>

            {rejection && (
              // `role="alert"` so the message is announced rather than only
              // drawn — the field is the one thing on this screen that fails.
              <Text id="cf-entry-domain-error" type="danger" role="alert">
                {rejection.message}
              </Text>
            )}

            {alreadySetUp && (
              <Text
                id="cf-entry-domain-warning"
                role="alert"
                style={{ color: token.colorWarningText, fontSize: token.fontSize }}
              >
                {alreadySetUp.ownership === 'current_account' ? (
                  <>
                    Prerender is already set up on <strong>{alreadySetUp.domain}</strong>.{' '}
                    <br />
                    To confirm it&apos;s working,{' '}
                    <Button
                      type="link"
                      onClick={() => onVerifyIntegration?.(alreadySetUp.domain)}
                      style={{
                        padding: 0,
                        height: 'auto',
                        color: token.colorPrimary,
                        textDecoration: 'underline',
                      }}
                    >
                      verify your integration
                    </Button>
                    .
                  </>
                ) : (
                  <>
                    If <strong>{alreadySetUp.domain}</strong> belongs to you, add it to
                    your account first, then try again. If you need a hand,{' '}
                    <Button
                      type="link"
                      onClick={() => {
                        track(
                          CONTACT_SUPPORT_CLICKED,
                          eventProperties(alreadySetUp.domain),
                        );
                        openHubSpotForm();
                      }}
                      style={{
                        padding: 0,
                        height: 'auto',
                        color: token.colorPrimary,
                        textDecoration: 'underline',
                      }}
                    >
                      contact support
                    </Button>
                    .
                  </>
                )}
              </Text>
            )}

            {betaClosed && (
              <Alert
                type="info"
                showIcon
                role="alert"
                message={BETA_CLOSED_TITLE}
                description={`${BETA_CLOSED_REASON} You can still integrate using the guides or Nexus below.`}
              />
            )}

            {probeFailed !== undefined && (
              <Alert
                type="error"
                showIcon
                role="alert"
                message="We couldn't check your domain"
                description={
                  <>
                    {probeFailureDescription(probeFailed)}{' '}
                    {/* The retry control PRE-3289 requires by name. It was a
                        `Typography.Link` with an `onClick` and no `href`, so a
                        keyboard could not reach the only way out of this
                        state. */}
                    <Button type="link" onClick={onAnalyze} style={{ padding: 0 }}>
                      Try again
                    </Button>
                    .
                  </>
                }
              />
            )}
          </Flex>

          <Text type="secondary" style={{ fontSize: token.fontSizeSM, textAlign: 'center' }}>
            We&apos;ll check your domain and show the steps before changing
            anything.
          </Text>
        </Flex>
      </Card>

      <Flex vertical gap={token.marginSM}>
        <Title level={3} style={{ marginBottom: 0 }}>
          Prefer a different way to set it up?
        </Title>
        <Card styles={{ body: { padding: token.paddingXL } }}>
          <Flex gap={token.marginMD} wrap="wrap">
            <Button
              style={optionStyle}
              onClick={() => openNexusOnTheGuides({ fromStep: 'entry', ...domainForNexusOf(domain) })}
            >
              <Flex justify="space-between" align="center" gap={token.marginSM} style={{ width: '100%' }}>
                <Flex align="center" gap={token.marginXS}>
                  <Flex align="center" justify="center" style={iconBadgeStyle} aria-hidden="true">
                    <NexusLogo size={token.sizeLG} />
                  </Flex>
                  <Flex vertical align="flex-start">
                    <Text strong>{CLOUDFLARE_NEXUS_OPTION_LABEL}</Text>
                    <Text type="secondary">Let our AI Assistant walk you through it.</Text>
                  </Flex>
                </Flex>
                <ArrowRightOutlined style={{ color: token.colorPrimary }} />
              </Flex>
            </Button>

            <Button style={optionStyle} onClick={() => navigate(guidesLocation(location.search))}>
              <Flex justify="space-between" align="center" gap={token.marginSM} style={{ width: '100%' }}>
                <Flex align="center" gap={token.marginXS}>
                  <Flex align="center" justify="center" style={iconBadgeStyle} aria-hidden="true">
                    <ReadOutlined style={{ fontSize: token.sizeLG, color: token.colorText }} />
                  </Flex>
                  <Flex vertical align="flex-start">
                    <Text strong>Follow the integration guides</Text>
                    <Text type="secondary">Step-by-step guides for your stack.</Text>
                  </Flex>
                </Flex>
                <ArrowRightOutlined style={{ color: token.colorPrimary }} />
              </Flex>
            </Button>
          </Flex>
        </Card>
      </Flex>

      {onGoBack && (
        <Flex>
          <Button type="link" icon={<LeftOutlined />} onClick={onGoBack}>
            Back
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

export default CloudflareEntryStep;
